import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import SEO from "@/components/site/SEO";

/**
 * Shared layout for all seven legal documents.
 * - Renders document title, effective date, version chip
 * - Includes "Back to home" link and print button
 * - Print styles applied via CSS (see index.css @media print rules)
 * - Contact block appears at the bottom of every doc
 */
export default function LegalPageLayout({
  document,
  seoDescription,
  children,
}) {
  const { label, version, effectiveDate, path } = document;
  const onPrint = () => window.print();
  return (
    <div className="max-w-3xl mx-auto px-6 py-14 legal-doc" data-testid={`legal-${document.id}-page`}>
      <SEO title={label} description={seoDescription} path={path} />

      <div className="print:hidden flex items-center justify-between mb-8">
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.14em] text-[#8C7A70] hover:text-[#2A1C14] inline-flex items-center gap-2"
          data-testid="legal-back-home"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to Hannah Howard Funding
        </Link>
        <button
          type="button"
          onClick={onPrint}
          className="text-xs uppercase tracking-[0.14em] text-[#8C7A70] hover:text-[#2A1C14] inline-flex items-center gap-2"
          data-testid="legal-print"
        >
          <Printer size={14} strokeWidth={1.5} /> Print
        </button>
      </div>

      <header className="mb-10">
        <span className="overline">Legal</span>
        <h1 className="font-serif-lux text-4xl sm:text-5xl mt-4 text-[#2A1C14] leading-tight">
          {label}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-[#8C7A70]">
          <span data-testid={`legal-${document.id}-effective`}>
            Effective Date: {effectiveDate}
          </span>
          <span aria-hidden="true">·</span>
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-[#F4EEE8] px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-[#5A3B31]"
            data-testid={`legal-${document.id}-version`}
          >
            Version {version}
          </span>
        </div>
      </header>

      <article className="prose-legal text-[#2A1C14]">{children}</article>

      <footer className="mt-16 pt-8 border-t border-[#E8DFD8]" data-testid={`legal-${document.id}-contact`}>
        <p className="overline mb-3">Contact</p>
        <p className="text-sm text-[#5A3B31] leading-relaxed">
          <strong className="text-[#2A1C14]">Hannah Howard Funding</strong>
          <br />
          Email:{" "}
          <a
            href="mailto:hello@hannahhowardfunding.com"
            className="underline hover:text-[#2A1C14]"
            target="_blank"
            rel="noopener noreferrer"
          >
            hello@hannahhowardfunding.com
          </a>
          <br />
          Phone:{" "}
          <a href="tel:+18709199520" className="underline hover:text-[#2A1C14]">
            (870) 919-9520
          </a>
          <br />
          Mailing Address: 495 Brickell Ave, Miami, FL 33131
        </p>
      </footer>
    </div>
  );
}

// Section wrappers used by the individual legal documents to keep numbering,
// spacing, and heading typography consistent.
export function Sec({ n, title, children }) {
  return (
    <section className="mb-8 legal-section">
      <h2 className="font-serif-lux text-2xl text-[#2A1C14] mb-3">
        {n ? `${n}. ` : ""}
        {title}
      </h2>
      <div className="space-y-3 text-[#2A1C14] leading-relaxed">{children}</div>
    </section>
  );
}

export function SubSec({ title, children }) {
  return (
    <div className="mt-4">
      <h3 className="font-semibold text-[#2A1C14] mb-2">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

// Bullet list helper - always renders as an unordered list with our brand styling
export function List({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1.5 text-[#2A1C14]">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}
